import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { toast } from 'react-toastify';
import { CheckCircleFilled } from '@ant-design/icons';

import { externalLinks, COMMON_CONTAINER_ID } from '@/config';
import { useCopyToClipboard, useOpenChat } from '@/hooks';
import { useAppSelector } from '@/redux/hooks';
import { profileSelectors } from '@/redux/selectors';

import { Button, SvgIcon, WebhookForm } from '@/components';

import styles from './ApiKeys.module.sass';

interface StatusBadgeProps {
  active: boolean;
}

const StatusBadge: FC<StatusBadgeProps> = ({ active }) => {
  const { t } = useTranslation();

  return (
    <span
      className={cx(styles.badge, {
        [styles.active]: active,
        [styles.disabled]: !active,
      })}
    >
      <span className={styles.dot} />
      {active
        ? t('translation.api_status_active')
        : t('translation.api_status_disabled')}
    </span>
  );
};

interface CopyFieldProps {
  label: string;
  value: string;
}

const CopyField: FC<CopyFieldProps> = ({ label, value }) => {
  const { t } = useTranslation();
  const [, copy] = useCopyToClipboard(false);

  const handleCopy = async () => {
    const success = await copy(value);

    if (success) {
      toast.success(`${value} ${t('translation.is_copied_to_clipboard')}`, {
        containerId: COMMON_CONTAINER_ID,
        icon: <CheckCircleFilled className={styles.copyToastIcon} />,
        className: styles.copyToast,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <div className={styles.value}>
        <span className={styles.mono}>{value}</span>
        <button
          type="button"
          className={styles.copyBtn}
          onClick={handleCopy}
          aria-label={label}
        >
          <SvgIcon name="copy-outline" className={styles.copyIcon} />
        </button>
      </div>
    </div>
  );
};

interface ApiKeysProps {}

const ApiKeys: FC<ApiKeysProps> = () => {
  const { t } = useTranslation();
  const { handleOpenChat } = useOpenChat();

  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const hasApiAccess = useAppSelector(profileSelectors.hasApiAccess);
  const webhookUrl = useAppSelector(profileSelectors.webhookUrlSetting);

  const [isApprovalOpen, setIsApprovalOpen] = useState(false);
  const [isWebhookEditing, setIsWebhookEditing] = useState(false);

  const renderWebhookBody = () => {
    if (isWebhookEditing) {
      return <WebhookForm onClose={() => setIsWebhookEditing(false)} />;
    }

    if (webhookUrl) {
      return (
        <div className={styles.webhookRow}>
          <div className={styles.webhookValue}>
            <span className={styles.dot} />
            <span className={styles.mono}>{webhookUrl}</span>
          </div>
          <Button
            type="secondary"
            corner="rect"
            className={styles.editBtn}
            onClick={() => setIsWebhookEditing(true)}
          >
            {t('translation.edit')}
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.webhookEmpty}>
        <SvgIcon name="info-outline" className={styles.infoIcon} />
        <span>{t('translation.api_webhook_not_set_desc')}</span>
      </div>
    );
  };

  return (
    <div className={styles.apiKeys}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>
            {t('translation.user_acess_key_title')}
          </h2>
          <StatusBadge active={hasApiAccess} />
        </div>

        {!hasApiAccess && (
          <Button
            type="primary"
            corner="rect"
            className={styles.action}
            onClick={() => setIsApprovalOpen((prev) => !prev)}
          >
            <SvgIcon name="key" className={styles.actionIcon} />
            {t('translation.get_access_key_link')}
          </Button>
        )}
      </div>

      {hasApiAccess ? (
        <>
          <div className={styles.credentials}>
            <CopyField
              label={t('translation.user_access_id_label')}
              value={profileData?.accessId ?? ''}
            />
            <div className={styles.divider} />
            <CopyField
              label={t('translation.user_access_key_label')}
              value={profileData?.accessKey ?? ''}
            />
          </div>

          <a
            href={externalLinks.API_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.docLink}
          >
            <SvgIcon name="book" className={styles.docIcon} />
            <span className={styles.docText}>
              {t('translation.api_documentation_link', 'AMLBot Documentation')}
            </span>
          </a>

          <div className={styles.divider} />

          <div className={styles.webhook}>
            <div className={styles.webhookHead}>
              <h3 className={styles.webhookTitle}>
                {t('translation.webhook_label')}
              </h3>
              {!webhookUrl && !isWebhookEditing && (
                <Button
                  type="secondary"
                  corner="rect"
                  className={styles.action}
                  onClick={() => setIsWebhookEditing(true)}
                >
                  <SvgIcon name="webhook" className={styles.actionIcon} />
                  {t('translation.change_webhook_btn')}
                </Button>
              )}
            </div>

            {renderWebhookBody()}
          </div>
        </>
      ) : (
        <>
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              <SvgIcon name="key" className={styles.emptyIconSvg} />
            </div>
            <div className={styles.emptyTitle}>
              {t('translation.api_access_key_not_set_title')}
            </div>
            <div className={styles.emptyText}>
              {t('translation.api_access_disabled_desc')}
            </div>
          </div>

          {isApprovalOpen && (
            <div className={styles.approval}>
              <div className={styles.approvalInfo}>
                <div className={styles.approvalIcon}>
                  <SvgIcon name="message" className={styles.approvalIconSvg} />
                </div>
                <div>
                  <div className={styles.approvalTitle}>
                    {t('translation.api_approval_title')}
                  </div>
                  <div className={styles.approvalText}>
                    {t('translation.api_approval_desc')}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className={styles.approvalBtn}
                onClick={handleOpenChat}
              >
                <SvgIcon name="telegram" className={styles.approvalBtnIcon} />
                {t('translation.contact_support')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApiKeys;
